import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Container,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMsal } from '@azure/msal-react';

interface EntraEndpointsExplorerProps {
  onBack: () => void;
}

interface EndpointItem {
  name: string;
  url: string;
  description: string;
  category: string;
  variables?: string[];
  example?: string;
  docUrl?: string;
  tags?: string[];
}

const EntraEndpointsExplorer: React.FC<EntraEndpointsExplorerProps> = ({ onBack }) => {
  const { accounts } = useMsal();
  const currentTenantId = accounts.length > 0 ? (accounts[0] as any).tenantId : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCloud, setSelectedCloud] = useState<'public' | 'government' | 'china' | 'germany'>('public');
  const [selectedTab, setSelectedTab] = useState(0);
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);
  const [tenantIdInput, setTenantIdInput] = useState(currentTenantId || '{tenant-id}');
  const [appIdInput, setAppIdInput] = useState('{client-id}');
  const [policyNameInput, setPolicyNameInput] = useState('{policy-name}');

  // Cloud-specific base URLs
  const cloudBases = {
    public: {
      login: 'https://login.microsoftonline.com',
      graph: 'https://graph.microsoft.com',
      management: 'https://management.azure.com',
      vault: 'https://vault.azure.net',
      storage: 'https://{account}.blob.core.windows.net',
      b2c: 'https://{tenant}.b2clogin.com'
    },
    government: {
      login: 'https://login.microsoftonline.us',
      graph: 'https://graph.microsoft.us',
      management: 'https://management.usgovcloudapi.net',
      vault: 'https://vault.usgovcloudapi.net',
      storage: 'https://{account}.blob.core.usgovcloudapi.net',
      b2c: 'https://{tenant}.b2clogin.us'
    },
    china: {
      login: 'https://login.chinacloudapi.cn',
      graph: 'https://microsoftgraph.chinacloudapi.cn',
      management: 'https://management.chinacloudapi.cn',
      vault: 'https://vault.azure.cn',
      storage: 'https://{account}.blob.core.chinacloudapi.cn',
      b2c: 'https://{tenant}.b2clogin.cn'
    },
    germany: {
      login: 'https://login.microsoftonline.de',
      graph: 'https://graph.microsoft.de',
      management: 'https://management.microsoftazure.de',
      vault: 'https://vault.microsoftazure.de',
      storage: 'https://{account}.blob.core.cloudapi.de',
      b2c: 'https://{tenant}.b2clogin.de'
    }
  };

  const base = cloudBases[selectedCloud];

  // Define all endpoints
  const endpoints: EndpointItem[] = useMemo(() => [
    // OAuth 2.0 / OpenID Connect Endpoints
    {
      name: 'Authorization Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/v2.0/authorize`,
      description: 'OAuth 2.0 authorization endpoint for interactive user sign-in',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      example: `${base.login}/common/oauth2/v2.0/authorize?client_id=${appIdInput}&response_type=code&redirect_uri=...`,
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-auth-code-flow',
      tags: ['v2.0', 'authorization', 'interactive']
    },
    {
      name: 'Token Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/v2.0/token`,
      description: 'OAuth 2.0 token endpoint for exchanging authorization codes or credentials for tokens',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      example: `POST ${base.login}/common/oauth2/v2.0/token`,
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-auth-code-flow#request-an-access-token',
      tags: ['v2.0', 'token', 'back-channel']
    },
    {
      name: 'Device Code Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/v2.0/devicecode`,
      description: 'Initiate device code flow for devices without browsers',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-device-code',
      tags: ['v2.0', 'device-code', 'browserless']
    },
    {
      name: 'OIDC Discovery Document (v2.0)',
      url: `${base.login}/${tenantIdInput}/v2.0/.well-known/openid-configuration`,
      description: 'OpenID Connect metadata endpoint listing all OAuth 2.0 endpoints and capabilities',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-protocols-oidc#fetch-the-openid-configuration-document',
      tags: ['v2.0', 'discovery', 'metadata']
    },
    {
      name: 'JWKS Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/discovery/v2.0/keys`,
      description: 'JSON Web Key Set (JWKS) for token signature verification',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/access-tokens#validate-tokens',
      tags: ['v2.0', 'jwks', 'signature-verification']
    },
    {
      name: 'Logout Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/v2.0/logout`,
      description: 'End user session and sign out',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      example: `${base.login}/common/oauth2/v2.0/logout?post_logout_redirect_uri=...`,
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v2-protocols-oidc#send-a-sign-out-request',
      tags: ['v2.0', 'logout', 'sign-out']
    },
    {
      name: 'UserInfo Endpoint (v2.0)',
      url: `${base.login}/${tenantIdInput}/openid/userinfo`,
      description: 'OIDC UserInfo endpoint for retrieving user claims',
      category: 'OAuth 2.0 & OIDC',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/userinfo',
      tags: ['v2.0', 'oidc', 'userinfo']
    },

    // Legacy v1.0 Endpoints
    {
      name: 'Authorization Endpoint (v1.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/authorize`,
      description: 'Legacy v1.0 authorization endpoint (use v2.0 for new apps)',
      category: 'Legacy v1.0',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v1-protocols-oauth-code',
      tags: ['v1.0', 'legacy', 'authorization']
    },
    {
      name: 'Token Endpoint (v1.0)',
      url: `${base.login}/${tenantIdInput}/oauth2/token`,
      description: 'Legacy v1.0 token endpoint (use v2.0 for new apps)',
      category: 'Legacy v1.0',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/v1-protocols-oauth-code',
      tags: ['v1.0', 'legacy', 'token']
    },
    {
      name: 'OIDC Discovery Document (v1.0)',
      url: `${base.login}/${tenantIdInput}/.well-known/openid-configuration`,
      description: 'Legacy v1.0 OpenID Connect metadata',
      category: 'Legacy v1.0',
      variables: ['tenant-id'],
      tags: ['v1.0', 'legacy', 'discovery']
    },

    // Microsoft Graph API
    {
      name: 'Microsoft Graph API (v1.0)',
      url: `${base.graph}/v1.0`,
      description: 'Stable Microsoft Graph API endpoint for production workloads',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/overview',
      tags: ['graph', 'stable', 'v1.0']
    },
    {
      name: 'Microsoft Graph API (beta)',
      url: `${base.graph}/beta`,
      description: 'Preview Microsoft Graph API endpoint with latest features (not for production)',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/overview',
      tags: ['graph', 'preview', 'beta']
    },
    {
      name: 'Graph - Users',
      url: `${base.graph}/v1.0/users`,
      description: 'List and manage user accounts',
      category: 'Microsoft Graph',
      example: `GET ${base.graph}/v1.0/users?$select=displayName,mail&$top=10`,
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/user-list',
      tags: ['graph', 'users']
    },
    {
      name: 'Graph - Applications',
      url: `${base.graph}/v1.0/applications`,
      description: 'Manage application registrations',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/application-list',
      tags: ['graph', 'applications']
    },
    {
      name: 'Graph - Service Principals',
      url: `${base.graph}/v1.0/servicePrincipals`,
      description: 'Manage enterprise applications (service principals)',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/serviceprincipal-list',
      tags: ['graph', 'service-principals']
    },
    {
      name: 'Graph - Groups',
      url: `${base.graph}/v1.0/groups`,
      description: 'List and manage groups',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/group-list',
      tags: ['graph', 'groups']
    },
    {
      name: 'Graph - Directory Objects',
      url: `${base.graph}/v1.0/directoryObjects`,
      description: 'Access directory objects (users, groups, apps, service principals)',
      category: 'Microsoft Graph',
      docUrl: 'https://learn.microsoft.com/en-us/graph/api/directoryobject-list',
      tags: ['graph', 'directory']
    },

    // Azure Resource Manager
    {
      name: 'Azure Resource Manager',
      url: base.management,
      description: 'Azure Resource Manager (ARM) API for managing Azure resources',
      category: 'Azure Management',
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/resources/',
      tags: ['arm', 'management']
    },
    {
      name: 'ARM - Subscriptions',
      url: `${base.management}/subscriptions`,
      description: 'List Azure subscriptions',
      category: 'Azure Management',
      example: `GET ${base.management}/subscriptions?api-version=2022-12-01`,
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/resources/subscriptions/list',
      tags: ['arm', 'subscriptions']
    },
    {
      name: 'ARM - Resource Groups',
      url: `${base.management}/subscriptions/{subscription-id}/resourcegroups`,
      description: 'List resource groups in a subscription',
      category: 'Azure Management',
      variables: ['subscription-id'],
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/resources/resource-groups/list',
      tags: ['arm', 'resource-groups']
    },

    // Azure Key Vault
    {
      name: 'Key Vault - Secrets',
      url: `https://{vault-name}${base.vault.replace('https://vault', '')}/secrets`,
      description: 'Access secrets stored in Azure Key Vault',
      category: 'Azure Services',
      variables: ['vault-name'],
      example: `GET https://myvault${base.vault.replace('https://vault', '')}/secrets/{secret-name}?api-version=7.4`,
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/keyvault/secrets/get-secret',
      tags: ['key-vault', 'secrets']
    },
    {
      name: 'Key Vault - Keys',
      url: `https://{vault-name}${base.vault.replace('https://vault', '')}/keys`,
      description: 'Manage cryptographic keys in Azure Key Vault',
      category: 'Azure Services',
      variables: ['vault-name'],
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/keyvault/keys/get-key',
      tags: ['key-vault', 'keys']
    },

    // Azure Storage
    {
      name: 'Blob Storage',
      url: base.storage,
      description: 'Azure Blob Storage API',
      category: 'Azure Services',
      variables: ['account'],
      example: `GET https://myaccount${base.storage.replace('https://{account}', '')}/mycontainer/myblob`,
      docUrl: 'https://learn.microsoft.com/en-us/rest/api/storageservices/blob-service-rest-api',
      tags: ['storage', 'blob']
    },

    // Azure AD B2C
    {
      name: 'B2C Authorization Endpoint',
      url: `${base.b2c}/{tenant}.onmicrosoft.com/${policyNameInput}/oauth2/v2.0/authorize`,
      description: 'Azure AD B2C authorization endpoint for custom policies',
      category: 'Azure AD B2C',
      variables: ['tenant', 'policy-name'],
      example: `${base.b2c}/contoso.onmicrosoft.com/B2C_1_signupsignin1/oauth2/v2.0/authorize`,
      docUrl: 'https://learn.microsoft.com/en-us/azure/active-directory-b2c/authorization-code-flow',
      tags: ['b2c', 'authorization']
    },
    {
      name: 'B2C Token Endpoint',
      url: `${base.b2c}/{tenant}.onmicrosoft.com/${policyNameInput}/oauth2/v2.0/token`,
      description: 'Azure AD B2C token endpoint for custom policies',
      category: 'Azure AD B2C',
      variables: ['tenant', 'policy-name'],
      docUrl: 'https://learn.microsoft.com/en-us/azure/active-directory-b2c/authorization-code-flow',
      tags: ['b2c', 'token']
    },
    {
      name: 'B2C Discovery Document',
      url: `${base.b2c}/{tenant}.onmicrosoft.com/${policyNameInput}/v2.0/.well-known/openid-configuration`,
      description: 'Azure AD B2C OpenID Connect discovery document',
      category: 'Azure AD B2C',
      variables: ['tenant', 'policy-name'],
      docUrl: 'https://learn.microsoft.com/en-us/azure/active-directory-b2c/openid-connect',
      tags: ['b2c', 'discovery']
    },

    // SAML & Federation
    {
      name: 'SAML Metadata',
      url: `${base.login}/${tenantIdInput}/federationmetadata/2007-06/federationmetadata.xml`,
      description: 'SAML 2.0 federation metadata document',
      category: 'SAML & Federation',
      variables: ['tenant-id'],
      docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/single-sign-on-saml-protocol',
      tags: ['saml', 'metadata', 'federation']
    },
    {
      name: 'WS-Federation Metadata',
      url: `${base.login}/${tenantIdInput}/federationmetadata/2007-06/federationmetadata.xml`,
      description: 'WS-Federation metadata endpoint',
      category: 'SAML & Federation',
      variables: ['tenant-id'],
      tags: ['ws-fed', 'metadata', 'federation']
    },
  ], [base, tenantIdInput, appIdInput, policyNameInput]);

  // Group endpoints by category
  const categories = useMemo(() => {
    const cats: Record<string, EndpointItem[]> = {};
    endpoints.forEach(ep => {
      if (!cats[ep.category]) cats[ep.category] = [];
      cats[ep.category].push(ep);
    });
    return cats;
  }, [endpoints]);

  // Filter endpoints based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    
    const filtered: Record<string, EndpointItem[]> = {};
    const query = searchQuery.toLowerCase();
    
    Object.entries(categories).forEach(([category, items]) => {
      const matchingItems = items.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.url.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query))
      );
      if (matchingItems.length > 0) {
        filtered[category] = matchingItems;
      }
    });
    
    return filtered;
  }, [categories, searchQuery]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(label);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const tabCategories = [
    'OAuth 2.0 & OIDC',
    'Microsoft Graph',
    'Azure Management',
    'Azure Services',
    'Azure AD B2C',
    'SAML & Federation',
    'Legacy v1.0'
  ];

  const currentCategory = tabCategories[selectedTab];
  const displayCategories = searchQuery.trim() 
    ? filteredCategories 
    : { [currentCategory]: categories[currentCategory] || [] };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Copied notification */}
      {copiedEndpoint && (
        <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, bgcolor: 'success.main', color: 'white', px: 3, py: 1.5, borderRadius: 2, boxShadow: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon />
          <Typography variant="body2">Copied: {copiedEndpoint}</Typography>
        </Box>
      )}

      {/* Header */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f4f8fb', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderColor: 'primary.main', color: 'primary.main' }
            }}
          >
            Back
          </Button>
          <Box sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", letterSpacing: '-0.01em' }}>
              Entra ID Endpoints Explorer
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif" }}>
              Comprehensive directory of Microsoft Entra ID, Azure AD B2C, Graph API, and Azure service endpoints
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Cloud & Variable Configuration */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <CloudIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Configuration</Typography>
          </Box>
          
          <Grid container spacing={3}>
            {/* Cloud Selection */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cloud Environment</InputLabel>
                <Select
                  value={selectedCloud}
                  label="Cloud Environment"
                  onChange={(e) => setSelectedCloud(e.target.value as any)}
                >
                  <MenuItem value="public">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PublicIcon fontSize="small" />
                      Azure Public Cloud
                    </Box>
                  </MenuItem>
                  <MenuItem value="government">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon fontSize="small" />
                      Azure Government (US)
                    </Box>
                  </MenuItem>
                  <MenuItem value="china">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudIcon fontSize="small" />
                      Azure China (21Vianet)
                    </Box>
                  </MenuItem>
                  <MenuItem value="germany">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudIcon fontSize="small" />
                      Azure Germany
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Variable Inputs */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Tenant ID"
                value={tenantIdInput}
                onChange={(e) => setTenantIdInput(e.target.value)}
                fullWidth
                size="small"
                placeholder="common / organizations / {guid}"
                helperText="Used in: {tenant-id}"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Client/App ID"
                value={appIdInput}
                onChange={(e) => setAppIdInput(e.target.value)}
                fullWidth
                size="small"
                placeholder="{client-id}"
                helperText="Used in examples"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="B2C Policy Name"
                value={policyNameInput}
                onChange={(e) => setPolicyNameInput(e.target.value)}
                fullWidth
                size="small"
                placeholder="B2C_1_signupsignin1"
                helperText="Used in: {policy-name}"
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Variables in URLs like <code>{'{tenant-id}'}</code>, <code>{'{client-id}'}</code> will be replaced with your configured values above when copying endpoints.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Search */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search endpoints by name, description, URL, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Category Tabs */}
      {!searchQuery.trim() && (
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {tabCategories.map((cat, idx) => (
              <Tab
                key={cat}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cat}
                    <Chip size="small" label={categories[cat]?.length || 0} />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Endpoints Display */}
      {Object.entries(displayCategories).map(([category, items]) => (
        <Box key={category} sx={{ mb: 4 }}>
          {searchQuery.trim() && (
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
              {category}
            </Typography>
          )}
          
          <Grid container spacing={2}>
            {items.map((endpoint, idx) => (
              <Grid item xs={12} key={idx}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ApiIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                          {endpoint.name}
                        </Typography>
                        {endpoint.tags && endpoint.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {endpoint.description}
                      </Typography>
                    </Box>
                    <Tooltip title="Copy endpoint URL">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(endpoint.url, endpoint.name)}
                        sx={{ ml: 1 }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* URL Display */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: '#f8f9fa',
                      borderRadius: 1,
                      mb: endpoint.example || endpoint.docUrl ? 1.5 : 0
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                        color: 'text.primary'
                      }}
                    >
                      {endpoint.url}
                    </Typography>
                  </Paper>

                  {/* Example */}
                  {endpoint.example && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Example:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {endpoint.example}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Variables */}
                  {endpoint.variables && endpoint.variables.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Variables:{' '}
                      </Typography>
                      {endpoint.variables.map(v => (
                        <Chip key={v} label={`{${v}}`} size="small" sx={{ mr: 0.5, height: 18, fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  )}

                  {/* Documentation Link */}
                  {endpoint.docUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkIcon fontSize="small" color="action" />
                      <Typography
                        component="a"
                        href={endpoint.docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        Documentation
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* No Results */}
      {searchQuery.trim() && Object.keys(filteredCategories).length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SearchIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No endpoints found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search query or browse by category
          </Typography>
        </Box>
      )}

      {/* About Section */}
      <Accordion
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: '#f9f9f9',
          mt: 4,
          '&:before': { display: 'none' },
          border: '1px solid #e2e8f0'
        }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>About Entra ID Endpoints Explorer</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            A comprehensive reference tool for Microsoft Entra ID (formerly Azure AD), Azure AD B2C, Microsoft Graph, and Azure service endpoints across all cloud environments.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              What you can do:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Browse Endpoints by Category:</strong> OAuth 2.0, OIDC, Microsoft Graph, Azure Management, B2C, SAML, and more
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Multi-Cloud Support:</strong> Switch between Azure Public, Government (US), China (21Vianet), and Germany clouds
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Variable Substitution:</strong> Configure tenant ID, client ID, and other variables to see personalized endpoint URLs
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Quick Copy:</strong> One-click copy for any endpoint URL with automatic variable replacement
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Search & Filter:</strong> Find endpoints quickly by name, description, URL, or tags
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Documentation Links:</strong> Direct links to official Microsoft Learn documentation for each endpoint
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Examples Included:</strong> See practical examples of how to use each endpoint with query parameters
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              Common use cases:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8 } }}>
              <li><Typography variant="body2">Quick reference when implementing authentication flows</Typography></li>
              <li><Typography variant="body2">Finding correct endpoints for different cloud environments</Typography></li>
              <li><Typography variant="body2">Discovering Graph API endpoints for specific resources</Typography></li>
              <li><Typography variant="body2">Configuring Azure AD B2C custom policies</Typography></li>
              <li><Typography variant="body2">Setting up SAML or WS-Federation integrations</Typography></li>
              <li><Typography variant="body2">Comparing v1.0 vs v2.0 endpoint differences</Typography></li>
              <li><Typography variant="body2">Documentation and training for development teams</Typography></li>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              💡 Pro tip:
            </Typography>
            <Typography variant="body2">
              Configure your tenant ID and client ID in the Configuration section to see personalized endpoint URLs. 
              Use "common", "organizations", or "consumers" for multi-tenant scenarios, or paste your specific tenant GUID.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default EntraEndpointsExplorer;
